import { z } from 'zod/v4';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    BaseVisualizationArgsSchema,
    createColumnTransform,
    createVisualizationResponseSchema,
    ExecutionMetadata,
    VisualizationParametersSchema,
} from '../validation/schemas';

// Table column schema matching Yup structure
export const TableColumnSchema = z.object({
    key: z.string(),
    header: z.string(),
    sortable: z.boolean().default(true),
    hidden: z.boolean().default(false),
    isMarkdown: z.boolean().default(false),
    format: z.string().default(''),
    hasPositiveColor: z.boolean().default(false),
    hasNegativeColor: z.boolean().default(false),
});

// Table group schema
export const TableGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    columns: z.array(TableColumnSchema).default([]),
});

// Table options schema matching Yup structure
export const TableOptionsSchema = z.object({
    enableSearch: z.boolean().default(true),
    enableColumnVisibility: z.boolean().default(false),
    enableResizableColumns: z.boolean().default(false),
    pageSize: z.number().min(25).max(200).default(100),
    groups: z.array(TableGroupSchema).default([]),
});

export const createTableArgsSchema = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const columnTransform = createColumnTransform(meta);
    return BaseVisualizationArgsSchema.extend({
        type: z.literal(StudioVisualizationType.Table),
        config: z.object({
            id: z.string().optional(),
            parameters: VisualizationParametersSchema.optional(),
            options: TableOptionsSchema.extend({
                groups: z
                    .array(
                        TableGroupSchema.extend({
                            columns: z
                                .array(
                                    TableColumnSchema.extend({
                                        key: columnTransform,
                                    }),
                                )
                                .default([]),
                        }),
                    )
                    .default([]),
            }),
        }),
    });
};

export const TableResponseSchema = createVisualizationResponseSchema(StudioVisualizationType.Table).extend({
    options: TableOptionsSchema,
    data: z.any(),
});

export type TableOptions = z.infer<typeof TableOptionsSchema>;
export type TableColumn = z.infer<typeof TableColumnSchema>;
export type TableGroup = z.infer<typeof TableGroupSchema>;
export type TableResponse = z.infer<typeof TableResponseSchema>;

// Export the generic type for the args schema
export type TableArgsSchema<TColumns extends string = string> = ReturnType<typeof createTableArgsSchema<TColumns>>;
export type TableArgs<TColumns extends string = string> = z.infer<TableArgsSchema<TColumns>>;
